/* eslint-disable react-hooks/exhaustive-deps */
import { Box, styled, Theme, Typography } from "@mui/material";
import { ReactComponent as SetUpPassKeyLogo } from "assets/icons/pass-key-set-up-circle.svg";
import { RetroButton } from "components/RetroButton";
import { useEmailAuth } from "hooks/useEmailAuth";
import { useEffect, useState } from "react";
import axiosService from "services/axios";
import useLayoutConfigStore from "store/layout-config-store/useLayoutConfigStore";
import useToastStore from "store/toast-store/useToastStore";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";

const EMAIL_REGEX = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

export const EmailCheckBlocker = ({
  variant,
  onVerified,
  onClickLogout,
  isEmailValid,
  isEmailSending,
  tgUserId,
  tgUserName,
  email,
  isOtpValid,
  isOtpVerifying,
  isEmailSent,
  userEmail,
  handleOnOTPChange,
  handleSendEmail,
  handleOnEmailChange,
  handleGoBack,
  handleVerifyOTP,
}: {
  variant: "init" | "update";
  onVerified?: () => void;
  onClickLogout: () => void;
  isEmailValid: boolean;
  isEmailSending: boolean;
  tgUserId: string;
  tgUserName: string;
  email: string;
  isOtpValid: boolean;
  isOtpVerifying: boolean;
  isEmailSent: boolean;
  userEmail: string;
  handleOnOTPChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSendEmail: () => void;
  handleOnEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleGoBack: () => void;
  handleVerifyOTP: () => void;
}): JSX.Element | null => {
  return !isEmailSent ? (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
          padding: "16px",
          textAlign: "center",
        }}>
        <Box>
          <SetUpPassKeyLogo />
        </Box>
        <Typography sx={{ marginBottom: "8px", maxWidth: "240px" }} variant="title-h2">
          {variant === "init" || !userEmail ? "INPUT EMAIL FOR RECOVERY" : "UPDATE RECOVERY EMAIL"}
        </Typography>
        <Box
          sx={{
            textAlign: "start",
            marginBottom: "8px",
            maxWidth: "372px",
            width: "100%",
          }}>
          <Box sx={{ marginBottom: "4px" }}>
            <Typography variant="body-sm" color="text.secondary">
              Email Address
            </Typography>
          </Box>
          <StyledInputV2
            key="email"
            defaultValue={email ?? ""}
            onChange={handleOnEmailChange}
            sx={{ textAlign: "start" }}
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            marginX: "auto",
            height: "56px",
            maxWidth: "372px",
            width: "100%",
            // paddingX: "12px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "4px",
          }}>
          <RetroButton
            variant={isEmailValid && !isEmailSending ? "primary" : "disabled"}
            onClick={handleSendEmail}
            disabled={!isEmailValid || isEmailSending}>
            <Box sx={{ padding: "8px" }}>
              <Typography variant="title-h3">NEXT</Typography>
            </Box>
          </RetroButton>
        </Box>
        {tgUserId && tgUserName && variant !== "update" ? (
          <Typography
            sx={{ marginY: "20px" }}
            variant="pre-title-2"
            color="text.system-red"
            onClick={onClickLogout}>
            Logout
          </Typography>
        ) : (
          <></>
        )}
      </Box>
    </>
  ) : (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
          padding: "16px",
          textAlign: "center",
        }}>
        <Box>
          <SetUpPassKeyLogo />
        </Box>
        <Typography sx={{ marginBottom: "34px", maxWidth: "300px" }} variant="title-h2">
          ENTER THE SECURITY ONE-TIME CODE
        </Typography>
        <Box
          sx={{
            textAlign: "start",
            marginBottom: "24px",
            maxWidth: "372px",
            width: "100%",
          }}>
          <Box sx={{ marginBottom: "4px" }}>
            <Typography variant="body-sm" color="text.secondary">
              One-time security code
            </Typography>
          </Box>
          <StyledInputV2 key="otp" onChange={handleOnOTPChange} sx={{ textAlign: "start" }} />
          <Typography variant="body-sm" color="brand.primary">
            A one-time security code is sent to {email}.
          </Typography>
        </Box>
        <Box
          sx={{
            position: "relative",
            marginX: "auto",
            height: "56px",
            maxWidth: "372px",
            width: "100%",
            // paddingX: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            gap: "12px",
          }}>
          <RetroButton variant="white" onClick={handleGoBack}>
            <Box>
              <Typography variant="title-h3">GO BACK</Typography>
            </Box>
          </RetroButton>
          <RetroButton
            variant={isOtpValid && !isOtpVerifying ? "primary" : "disabled"}
            onClick={handleVerifyOTP}
            disabled={!isOtpValid || isOtpVerifying}>
            <Box sx={{ padding: "8px" }}>
              <Typography variant="title-h3">CONFIRM</Typography>
            </Box>
          </RetroButton>
        </Box>
        {tgUserId && tgUserName && variant !== "update" ? (
          <Typography
            sx={{ marginY: "20px" }}
            variant="pre-title-2"
            color="text.system-red"
            onClick={onClickLogout}>
            Logout
          </Typography>
        ) : (
          <></>
        )}
      </Box>
    </>
  );
};

const TYPOGRAPHY_BODY_MD = (theme: Theme) => ({
  ...theme.typography["body-default"],
});

const StyledInputV2 = styled("input")(({ theme }) => ({
  width: "100%",
  height: "46px",
  border: `none`,
  textAlign: "end",
  backgroundColor: theme.palette.surface.silver,
  borderRadius: "8px",
  padding: "0 12px",
  opacity: "0.5",
  color: theme.palette.text.primary,
  ...TYPOGRAPHY_BODY_MD(theme as Theme),
  "&:focus, &:not(:placeholder-shown)": {
    opacity: "1",
    outline: "none",
  },
  "&::placeholder": {
    color: theme.palette.text.primary,
  },
}));
